import * as React from "react";
import styled, { keyframes } from "styled-components";

import { theme } from "~/theme";

import module1Img from "../assets/gfx/module1-bg1.jpg";
import module2Img from "../assets/gfx/module-2-image.jpg";
import module3Img from "../assets/gfx/module-3-image.jpg";
import module4Img from "../assets/gfx/module-4-image.jpg";
import doneImg from "../assets/gfx/done-image.jpg";
import trophy from "../assets/svg/trophy-gold.svg";

import rightArrow from "../assets/svg/rightArrow.svg";
import clock from "../assets/svg/clock.svg";

interface Data {
  imgSrc: string;
  title: string;
  body: JSX.Element;
  showFooter: boolean;
}

const data: Data[] = [
  {
    imgSrc: module1Img,
    title: "Module 1",
    body: <p>Introduction to workplace health</p>,
    showFooter: true,
  },
  {
    imgSrc: module2Img,
    title: "Module 2",
    body: <p>Motivating staff to be healthy</p>,
    showFooter: true,
  },
  {
    imgSrc: module3Img,
    title: "Module 3",
    body: <p>Best practice workplace health promotion</p>,
    showFooter: true,
  },
  {
    imgSrc: module4Img,
    title: "Module 4",
    body: <p>Planning your program</p>,
    showFooter: true,
  },
  {
    imgSrc: doneImg,
    title: "DONE!",
    body: (
      <>
        <p className="mb-4">Receive a certificate of completion</p>
        <img
          className="d-block mx-auto"
          src={trophy}
          alt="trophy"
          width={40}
          height={40}
        />
      </>
    ),
    showFooter: false,
  },
];

export const Hub_InteractiveTraining: React.FC<Record<string, never>> = () => {
  return (
    <CarouselContainer className="mx-auto">
      <Carousel>
        {data.map(({ imgSrc, title, body, showFooter }, idx) => (
          <CarouselItem num={idx} key={idx} className="card mb-0">
            <img className="card-img-top" src={imgSrc} alt={`${title} image`} />
            <div className="card-body">
              <h4 className="h5 card-title">{title}</h4>
              {body}
            </div>
            {showFooter && (
              <div
                className="card-footer"
                style={{ background: "transparent" }}
              >
                <img src={clock} alt="clock" width={20} height={20} />
                <small className="text-muted ml-2">20 mins</small>
              </div>
            )}
          </CarouselItem>
        ))}
      </Carousel>
    </CarouselContainer>
  );
};

const slideAcross = (translateX: number, marginX: number) => keyframes`
  0% {
    opacity: 0;
  }
  5% {
    opacity: 1;
    transform: translateX(0);
  }
  10% {
    opacity: 1;
    transform: translateX(0);
  }
  80% {
    opacity: 1;
    transform: translateX(calc(${translateX}% - ${marginX}rem));
  }
  85% {
    opacity: 1;
    transform: translateX(calc(${translateX}% - ${marginX}rem));;
  }
  90% {
    opacity: 0;
    transform: translateX(calc(${translateX}% - ${marginX}rem));;
  }
  100% {
    opacity: 0;
  }
`;

const showUp = keyframes`
  0% {
    opacity: 0;
    border: 2px solid transparent;
  }
  20% {
    opacity: 1;
    border: 2px solid transparent;
  }
  100% {
    opacity: 1;
    border: 2px solid ${theme.gold};
  }
`;

const arrowsIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  50% {
    opacity: 0.3;
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0px);
  }
`;

const CarouselContainer = styled.section`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  margin: 0 auto;
`;

const Carousel = styled.div`
  width: auto;
  display: flex;
  opacity: 0;
  animation: 20s linear 1s
    ${slideAcross(-100 * (data.length - 1), 5 * (data.length - 1))} infinite;

  @media screen and (min-width: 768px) {
    animation: 10s linear 1s ${slideAcross(-97, 0)} infinite;
  }

  @media screen and (min-width: 992px) {
    animation: none;
    opacity: 1;
  }
`;

const CarouselItem = styled.div<{num: number}>`
  min-width: 100%;
  border: 2px solid ${theme.gold};

  h4 {
    color: ${theme.warning};
  }

  &:not(:last-of-type) {
    margin-right: 5rem;
  }

  &:not(:last-of-type)::after {
    content: "";
    position: absolute;
    top: calc(50% - 1.5rem);
    right: calc(0% - 4rem);
    width: 3rem;
    height: 3rem;
    background-color: ${theme.info};
    background-image: url(${rightArrow});
    background-size: 45%;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 50%;
  }

  @media screen and (min-width: 768px) {
    min-width: 33%;
    &:not(:last-of-type) {
      margin-right: 3rem;
    }
    &:not(:last-of-type)::after {
      right: calc(-2px - 2.5rem);
      width: 2rem;
      height: 2rem;
    }
  }

  @media screen and (min-width: 992px) {
    min-width: calc(20% - 2rem);

    opacity: 0;
    border: 2px solid transparent;
  
    animation: 2s linear ${p => (p.num * 3)}s ${showUp} forwards;

    &:not(:last-of-type) {
      margin-right: 2rem;
    }

    &:not(:last-of-type)::after {
      right: calc(2px - 2rem);
      width: 1.5rem;
      height: 1.5rem;

      opacity: 0;
      animation: 500ms ease-in ${p => (p.num * 3) + 2}s ${arrowsIn} forwards;
    }
  }
`;
